import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  List,
  ListItem,
  ListItemText,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
} from "@mui/material";
import { MoreVert as MoreVertIcon } from "@mui/icons-material";
import GlassCard from "../../../components/common/GlassCard";
import { READING_TYPE_LABELS } from "../rpmReadingFields";

const RpmDeviceReadingsCard = ({
  rpmReadings = [],
  onAddRpmReading,
  onDeleteRpmReading,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedReading, setSelectedReading] = useState(null);

  const handleMenuClick = (event, reading) => {
    setAnchorEl(event.currentTarget);
    setSelectedReading(reading);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedReading(null);
  };

  const handleDelete = () => {
    if (selectedReading && onDeleteRpmReading) {
      onDeleteRpmReading(selectedReading.id);
    }
    handleMenuClose();
  };

  const getReadingTypeLabel = (type) => READING_TYPE_LABELS[type] || type;

  const getAlertColor = (alert_level) => {
    const alertColors = {
      critical: "error",
      high: "warning",
      normal: "success",
      low: "info",
    };
    return alertColors[alert_level?.toLowerCase()] || "default";
  };

  const formatReadingValue = (reading) => {
    if (reading.reading_type === "blood_pressure") {
      return `${reading.systolic || ""}/${reading.diastolic || ""} mmHg`;
    } else if (reading.reading_type === "glucose") {
      return `${reading.value || ""} mg/dL`;
    } else if (reading.reading_type === "weight") {
      return `${reading.value || ""} lbs`;
    } else if (reading.reading_type === "pulse_ox") {
      return `${reading.value || ""}% SpO2`;
    } else if (reading.reading_type === "heart_rate") {
      return `${reading.value || ""} bpm`;
    } else if (reading.reading_type === "temperature") {
      return `${reading.value || ""}°F`;
    } else if (reading.reading_type === "respiratory_rate") {
      return `${reading.value || ""} breaths/min`;
    }
    return reading.value || "N/A";
  };

  return (
    <GlassCard
      title="RPM Device Readings"
      addLabel="Add RPM Reading"
      onAdd={onAddRpmReading}
    >
      {rpmReadings && rpmReadings.length > 0 ? (
        <List dense>
          {rpmReadings.map((reading, index) => (
            <ListItem
              key={reading.id || index}
              sx={{
                borderRadius: 1,
                mb: 1,
                bgcolor: "rgba(255, 255, 255, 0.02)",
                "&:hover": {
                  bgcolor: "rgba(255, 255, 255, 0.05)",
                },
              }}
              secondaryAction={
                <IconButton
                  edge="end"
                  aria-label="Reading options"
                  size="small"
                  onClick={(e) => handleMenuClick(e, reading)}
                  data-action="open-rpm-reading-menu"
                  data-category="rpm-reading"
                  data-reading-id={reading.id}
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              }
            >
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {getReadingTypeLabel(reading.reading_type)}
                    </Typography>
                    {reading.alert_level && (
                      <Chip
                        label={reading.alert_level}
                        size="small"
                        color={getAlertColor(reading.alert_level)}
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    )}
                  </Box>
                }
                secondary={
                  <Box sx={{ mt: 0.5 }}>
                    <Typography variant="body2" display="block" color="text.primary">
                      {formatReadingValue(reading)}
                    </Typography>
                    {reading.reading_date && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {new Date(reading.reading_date).toLocaleString()}
                      </Typography>
                    )}
                    {reading.device_id && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Device: {reading.device_id}
                      </Typography>
                    )}
                    {reading.notes && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {reading.notes}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", py: 2 }}>
          No RPM device readings
        </Typography>
      )}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        data-category="rpm-reading-menu"
      >
        <MenuItem
          onClick={handleDelete}
          data-action="delete-rpm-reading"
          data-category="rpm-reading"
        >
          Delete Reading
        </MenuItem>
      </Menu>
    </GlassCard>
  );
};

RpmDeviceReadingsCard.propTypes = {
  rpmReadings: PropTypes.array,
  onAddRpmReading: PropTypes.func,
  onDeleteRpmReading: PropTypes.func,
};

export default RpmDeviceReadingsCard;
